import { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setClinicInfo,
  setLogo,
  clearLogo,
  nextStep,
  prevStep,
  selectClinicInfo,
  selectLogo,
} from "../../../store/slices/registrationSlice";
import {
  Buildings,
  Upload,
  X,
  ArrowLeft,
  ArrowRight,
} from "@phosphor-icons/react";
import Button from "../../ui/CustomClinikitButton";
import { CLINIC_TYPES } from "../../../data/clinicTypesConfig";
import { scrollToFirstError } from "../../../utils/formScroll";
import "./Steps.css";

function ClinicInfoStep() {
  const dispatch = useDispatch();
  const savedInfo = useSelector(selectClinicInfo);
  const savedLogo = useSelector(selectLogo);
  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    clinicName: savedInfo.clinicName || "",
    clinicType: savedInfo.clinicType || "",
    customTypeName: savedInfo.customTypeName || "",
    businessId: savedInfo.businessId || "",
    clinicAddress: savedInfo.clinicAddress || "",
    vatType: savedInfo.vatType || "",
  });

  const [logoPreview, setLogoPreview] = useState(savedLogo.preview || null);
  const [errors, setErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.clinicName.trim()) {
      newErrors.clinicName = "שדה חובה";
    }

    if (!formData.clinicType) {
      newErrors.clinicType = "יש לבחור סוג קליניקה";
    } else if (formData.clinicType === "other") {
      const trimmed = formData.customTypeName.trim();
      if (!trimmed) {
        newErrors.customTypeName = "יש להזין שם סוג קליניקה";
      } else if (trimmed.length < 2) {
        newErrors.customTypeName = "מינימום 2 תווים";
      } else if (trimmed.length > 50) {
        newErrors.customTypeName = "מקסימום 50 תווים";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleTypeSelect = (value) => {
    setFormData((prev) => ({
      ...prev,
      clinicType: value,
      // Clear custom name when switching away from "other"
      customTypeName: value === "other" ? prev.customTypeName : "",
    }));
    if (errors.clinicType || errors.customTypeName) {
      setErrors((prev) => ({ ...prev, clinicType: "", customTypeName: "" }));
    }
  };

  const handleFileSelect = (file) => {
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
        dispatch(setLogo({ file, preview: e.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    dispatch(clearLogo());
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleBack = () => {
    dispatch(setClinicInfo(formData));
    dispatch(prevStep());
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      dispatch(setClinicInfo(formData));
      dispatch(nextStep());
    } else {
      scrollToFirstError(formRef.current);
    }
  };

  return (
    <div className="step-container">
      <div className="step-header">
        <Buildings size={24} weight="duotone" className="step-icon" />
        <h2>פרטי הקליניקה</h2>
        <p>מידע בסיסי על הקליניקה שלכם</p>
      </div>

      <form ref={formRef} onSubmit={handleSubmit} className="step-form">
        <div className="form-group">
          <label className="required">שם הקליניקה</label>
          <input
            type="text"
            name="clinicName"
            value={formData.clinicName}
            onChange={handleChange}
            placeholder='לדוגמה: "קליניקת השפה"'
            className={errors.clinicName ? "error" : "clinic-name-input"}
          />
          {errors.clinicName && (
            <span className="field-error">{errors.clinicName}</span>
          )}
        </div>

        <div className="form-group">
          <label className="required">סוג הפרקטיקה</label>
          <div className="option-cards type-cards">
            {CLINIC_TYPES.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.value}
                  type="button"
                  className={`option-card ${
                    formData.clinicType === type.value ? "selected" : ""
                  }`}
                  onClick={() => handleTypeSelect(type.value)}
                >
                  <Icon
                    size={20}
                    weight={
                      formData.clinicType === type.value ? "fill" : "regular"
                    }
                  />
                  <span>{type.label}</span>
                </button>
              );
            })}
          </div>
          {errors.clinicType && (
            <span className="field-error">{errors.clinicType}</span>
          )}
          {formData.clinicType === "other" && (
            <div className="custom-type-name-wrapper">
              <input
                type="text"
                name="customTypeName"
                value={formData.customTypeName}
                onChange={handleChange}
                placeholder="הקלידו את סוג הקליניקה"
                maxLength={50}
                className={errors.customTypeName ? "error" : "clinic-name-input"}
                autoFocus
              />
              {errors.customTypeName && (
                <span className="field-error">{errors.customTypeName}</span>
              )}
            </div>
          )}
        </div>

        <div className="form-group">
          <label>
            לוגו הקליניקה
            <span className="optional-tag">לא חובה</span>
          </label>
          <div
            className={`logo-upload ${isDragging ? "dragging" : ""} ${
              logoPreview ? "has-logo" : ""
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !logoPreview && fileInputRef.current?.click()}
          >
            {logoPreview ? (
              <div className="logo-preview">
                <img src={logoPreview} alt="Logo preview" />
                <button
                  type="button"
                  className="remove-logo"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveLogo();
                  }}
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            ) : (
              <div className="upload-placeholder">
                <Upload size={24} weight="regular" />
                <span className="upload-text">גררו תמונה או לחצו לבחירה</span>
                <span className="upload-hint">PNG, JPG עד 2MB</span>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="file-input"
            />
          </div>
        </div>

        <div className="step-actions step-actions-between">
          <Button
            type="button"
            variant="ghost"
            size="medium"
            onClick={handleBack}
          >
            <ArrowRight size={18} weight="bold" />
            חזרה
          </Button>
          <Button type="submit" variant="primary" size="large">
            המשך
            <ArrowLeft size={18} weight="bold" />
          </Button>
        </div>
      </form>
    </div>
  );
}

export default ClinicInfoStep;
